import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TextField from "../../../components/Form/TextField";
import IconButton from "../../../components/Buttons/IconButton";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import Loader from "../../../components/Loaders/Loader";
import { ConfirmAlert, SimpleAlert } from "../../../components/Alerts/Alerts";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import {
  startDeleteCreativeTemplatePrompt,
  startListCreativeTemplatePrompts,
  startSaveCreativeTemplatePrompt,
} from "../../../actions/creativeTemplates";
import {
  BUTTON_CANCEL,
  BUTTON_DELETE,
  BUTTON_GO_BACK,
  BUTTON_SAVE,
  NAME,
  TEMPLATES_DELETE_PROMPT,
  TEMPLATES_DELETE_PROMPT_ERROR,
  TEMPLATES_GENERATE_PROMPT,
  TEMPLATES_GENERATE_PROMPT_NO_EXAMPLE,
  TEMPLATES_IMPORT_PROMPT,
  TEMPLATES_IMPORT_PROMPT_EMPTY,
  TEMPLATES_IMPORT_PROMPT_ERROR,
  TEMPLATES_IMPORT_PROMPT_TITLE,
  TEMPLATES_IMPORT_SELECTED,
  TEMPLATES_PROMPT,
  TEMPLATES_SAVE_PROMPT,
  TEMPLATES_SAVE_PROMPT_ERROR,
  TEMPLATES_SAVE_PROMPT_SUCCESS,
  TEMPLATES_SAVE_PROMPT_TITLE,
} from "../../../i18n/keysTranslations";
import { textFieldInputSx } from "../../../styles";
import { BORDER_RADIUS } from "../../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";

const optionalRules = { required: false };
const NAME_MAX_LENGTH = 80;
const PROMPT_MAX_LENGTH = 8000;

const hasLayerExample = ({ kind, originText, originImageUrl }) => {
  switch (kind) {
    case "text":
      return Boolean(String(originText || "").trim());
    case "image":
      return Boolean(String(originImageUrl || "").trim());
    default:
      return false;
  }
};

const PromptInstructionField = ({
  control,
  errors,
  disabled = false,
  nodeID = "",
  kind = "text",
  originText = "",
  originImageUrl = "",
  promptValue = "",
  onChangeField,
  onGenerate,
  isGenerating = false,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const nodeIDRef = useRef(nodeID);
  const importRequestRef = useRef(0);
  nodeIDRef.current = nodeID;
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [promptsLoadFailed, setPromptsLoadFailed] = useState(false);
  const [deletingID, setDeletingID] = useState("");
  const [selectedPromptID, setSelectedPromptID] = useState("");

  const {
    control: saveControl,
    handleSubmit,
    reset: resetSave,
    watch,
    formState: { errors: saveErrors },
  } = useForm({
    defaultValues: {
      name: "",
      prompt: "",
    },
  });
  const saveName = watch("name");
  const savePrompt = watch("prompt");
  const canSave =
    Boolean(String(saveName || "").trim()) &&
    Boolean(String(savePrompt || "").trim());

  const exampleReady = hasLayerExample({ kind, originText, originImageUrl });

  const applyPrompt = ({ prompt, requestNodeID }) => {
    if (requestNodeID !== nodeIDRef.current) return;
    onChangeField({
      key: "promptTemplate",
      value: prompt,
      nodeID: requestNodeID,
    });
  };

  const handleGenerate = () => {
    if (disabled || isGenerating || !exampleReady || !onGenerate) return;
    onGenerate({ nodeID, kind, originText, imageUrl: originImageUrl });
  };

  const openSave = () => {
    resetSave({
      name: "",
      prompt: String(promptValue || ""),
    });
    setIsSaveOpen(true);
  };

  const onSave = async ({ name, prompt }) => {
    const result = await dispatch(
      startSaveCreativeTemplatePrompt({ name, prompt })
    );
    if (!result?.ok) {
      SimpleAlert({
        title: t(TEMPLATES_SAVE_PROMPT_ERROR),
        icon: "error",
      });
      return;
    }
    setIsSaveOpen(false);
    SimpleAlert({
      title: t(TEMPLATES_SAVE_PROMPT_SUCCESS),
      icon: "success",
    });
  };

  const openImport = async () => {
    const requestID = importRequestRef.current + 1;
    importRequestRef.current = requestID;
    setIsImportOpen(true);
    setIsLoadingPrompts(true);
    setPromptsLoadFailed(false);
    setSelectedPromptID("");
    setSavedPrompts([]);
    const result = await dispatch(startListCreativeTemplatePrompts());
    if (importRequestRef.current !== requestID) return;
    setIsLoadingPrompts(false);
    if (!result?.ok) {
      setPromptsLoadFailed(true);
      SimpleAlert({
        title: t(TEMPLATES_IMPORT_PROMPT_ERROR),
        icon: "error",
      });
      return;
    }
    setSavedPrompts(result.prompts || []);
  };

  const onImportSelected = () => {
    const item = savedPrompts.find((entry) => entry.id === selectedPromptID);
    if (!item) return;
    applyPrompt({ prompt: item.prompt, requestNodeID: nodeID });
    setIsImportOpen(false);
  };

  const onDelete = async (event, promptID) => {
    event.stopPropagation();
    if (deletingID) return;
    const confirmed = await ConfirmAlert({
      title: t(TEMPLATES_DELETE_PROMPT),
      confirmButtonText: t(BUTTON_DELETE),
    });
    if (!confirmed?.isConfirmed) return;
    setDeletingID(promptID);
    const result = await dispatch(
      startDeleteCreativeTemplatePrompt({ promptID })
    );
    setDeletingID("");
    if (!result?.ok) {
      SimpleAlert({
        title: t(TEMPLATES_DELETE_PROMPT_ERROR),
        icon: "error",
      });
      return;
    }
    setSavedPrompts((current) =>
      current.filter((item) => item.id !== promptID)
    );
    setSelectedPromptID((current) => (current === promptID ? "" : current));
  };

  return (
    <>
      <Box
        sx={{
          ...textFieldInputSx["& .MuiInputBase-root"],
          overflow: "hidden",
          flexShrink: 0,
          pr: "8px",
          pb: "6px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            resize: "vertical",
            overflow: "hidden",
            height: 318,
            minHeight: 120,
            flexShrink: 0,
          }}
        >
          <TextField
            control={control}
            errors={errors}
            name="promptTemplate"
            label={t(TEMPLATES_PROMPT)}
            variant="filled"
            fullWidth
            multiline
            minRows={3}
            disabled={disabled}
            rules={optionalRules}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              "& .MuiInputBase-root.MuiFilledInput-root": {
                ...textFieldInputSx["& .MuiInputBase-root"],
                flex: 1,
                minHeight: 0,
                alignItems: "stretch",
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
                "&:hover, &.Mui-focused, &.Mui-disabled": {
                  backgroundColor: "transparent",
                },
                "&:before, &:after": {
                  display: "none",
                },
              },
              "& .MuiInputBase-inputMultiline": {
                height: "100% !important",
                minHeight: "0 !important",
                resize: "none",
                overflowY: "auto !important",
                backgroundColor: "transparent",
              },
            }}
            actionsChange={(value) =>
              onChangeField({ key: "promptTemplate", value })
            }
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-end",
              flexShrink: 0,
              pr: "12px",
            }}
          >
            <IconButton
              tooltipText={
                exampleReady
                  ? TEMPLATES_GENERATE_PROMPT
                  : TEMPLATES_GENERATE_PROMPT_NO_EXAMPLE
              }
              disabled={disabled || isGenerating || !exampleReady}
              loading={isGenerating}
              edge={false}
              onClick={handleGenerate}
              sx={{ p: "2px", width: 28, height: 28 }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              tooltipText={TEMPLATES_SAVE_PROMPT}
              disabled={disabled}
              edge={false}
              onClick={openSave}
              sx={{ p: "2px", width: 28, height: 28 }}
            >
              <SaveOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              tooltipText={TEMPLATES_IMPORT_PROMPT}
              disabled={disabled}
              edge={false}
              onClick={openImport}
              sx={{ p: "2px", width: 28, height: 28 }}
            >
              <CollectionsBookmarkOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
      <ModalBasicLayout
        modalOpen={isSaveOpen}
        onCloseModal={() => setIsSaveOpen(false)}
        title={t(TEMPLATES_SAVE_PROMPT_TITLE)}
        ariaLabel={t(TEMPLATES_SAVE_PROMPT_TITLE)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            name="name"
            control={saveControl}
            errors={saveErrors}
            label={t(NAME)}
            variant="filled"
            fullWidth
            autoFocus
            inputProps={{ maxLength: NAME_MAX_LENGTH }}
          />
          <TextField
            name="prompt"
            control={saveControl}
            errors={saveErrors}
            label={t(TEMPLATES_PROMPT)}
            variant="filled"
            fullWidth
            multiline
            minRows={4}
            inputProps={{ maxLength: PROMPT_MAX_LENGTH }}
          />
        </Box>
        <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 3, mt: 0.5 }}>
          <ContainerModalActions
            hasPadding={false}
            propsSecondaryButton={{
              showButton: true,
              text: BUTTON_CANCEL,
              onClick: () => setIsSaveOpen(false),
            }}
            propsPrimaryButton={{
              showButton: true,
              text: BUTTON_SAVE,
              disabled: !canSave,
              onClick: handleSubmit(onSave),
            }}
          />
        </DialogActions>
      </ModalBasicLayout>
      <ModalBasicLayout
        modalOpen={isImportOpen}
        onCloseModal={() => setIsImportOpen(false)}
        title={t(TEMPLATES_IMPORT_PROMPT_TITLE)}
        ariaLabel={t(TEMPLATES_IMPORT_PROMPT_TITLE)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        {isLoadingPrompts ? (
          <Loader hasMarginTop={false} size={28} fullWidth />
        ) : null}
        {!isLoadingPrompts && !promptsLoadFailed && !savedPrompts.length ? (
          <Typography variant="body2" color="text.secondary">
            {t(TEMPLATES_IMPORT_PROMPT_EMPTY)}
          </Typography>
        ) : null}
        {!isLoadingPrompts && savedPrompts.length ? (
          <RadioGroup
            value={selectedPromptID}
            onChange={(event) => setSelectedPromptID(event.target.value)}
          >
            {savedPrompts.map((item) => (
              <Box
                key={item.id}
                sx={{
                  mb: 1,
                  p: 1.5,
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  borderRadius: BORDER_RADIUS,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.5,
                }}
              >
                <FormControlLabel
                  value={item.id}
                  control={
                    <Radio
                      sx={{
                        p: 0,
                        mr: 1,
                        mt: "2px",
                      }}
                    />
                  }
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25,
                      }}
                    >
                      <Typography sx={{ fontWeight: 700 }}>
                        {item.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          whiteSpace: "pre-line",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.prompt}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    flex: 1,
                    m: 0,
                    minWidth: 0,
                    alignItems: "flex-start",
                    "& .MuiFormControlLabel-label": {
                      width: "100%",
                      minWidth: 0,
                    },
                  }}
                />
                <IconButton
                  tooltipText={TEMPLATES_DELETE_PROMPT}
                  disabled={Boolean(deletingID)}
                  loading={deletingID === item.id}
                  edge={false}
                  onClick={(event) => onDelete(event, item.id)}
                  sx={{ p: 0.5, mt: -0.25 }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ))}
          </RadioGroup>
        ) : null}
        <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 3, mt: 0.5 }}>
          <ContainerModalActions
            hasPadding={false}
            propsSecondaryButton={{
              showButton: true,
              text: BUTTON_GO_BACK,
              onClick: () => setIsImportOpen(false),
            }}
            propsPrimaryButton={{
              showButton: true,
              text: TEMPLATES_IMPORT_SELECTED,
              disabled: !selectedPromptID,
              onClick: onImportSelected,
            }}
          />
        </DialogActions>
      </ModalBasicLayout>
    </>
  );
};

export default PromptInstructionField;
